import styled from '@emotion/styled';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import React from 'react';
import { useTranslate } from 'hooks/useTranslate';

import Footer from 'components/Footer';
import Header from 'components/Header';
import BgLeft from 'assets/icons/download5.svg?react';
import BgRight from 'assets/icons/download6.svg?react';
import TrollGreetings from 'assets/images/careerTest/troll-greetings.png';
import Helmet from 'components/Helmet';
import LinearProgress from 'components/LinearProgress';
import PageContent from 'components/PageContent';
import PageTitle from 'components/PageTitle';
import platformsEndPoint from 'endPoints/platforms';
import MainLayout from 'layouts/MainLayout';
import useDataListFactory from 'services/dataList';
import SearchToggleInput from 'components/SearchToggleInput';
import { ResultsWrapper, ResultsControls } from 'components/ResultsLayout';
import { useDispatch, useSelector } from 'react-redux';
import { getEducationsDirections } from 'actions/educations';

import PlatformCard from './components/PlatformCard';
import useInfiniteScroll from 'hooks/useInfiniteScroll';
import SortIcon from 'assets/icons/iconoir_sort.svg?react';
import formatDirectionName from 'helpers/formatDirectionName';
import CardGridSkeleton from 'components/CardGridSkeleton';
const Breadcrumbs = React.lazy(() => import('components/BreadCrumbs'));

const useDataList = (props, options) => useDataListFactory(props, options);

const Title = styled(PageTitle)(() => ({
  userSelect: 'text',
  WebkitUserSelect: 'text',
  MozUserSelect: 'text',
  msUserSelect: 'text',
  cursor: 'text',
  '& *': {
    userSelect: 'text',
    WebkitUserSelect: 'text',
    MozUserSelect: 'text',
    msUserSelect: 'text',
    cursor: 'text'
  }
}));

const Content = styled(PageContent)(({ theme }) => ({
  paddingTop: 30,
  position: 'relative',
  overflow: 'hidden',
  zIndex: 1,
  [theme.breakpoints.down('lg')]: {
    paddingLeft: 20,
    paddingRight: 20
  },
  [theme.breakpoints.down('sm')]: {
    paddingBottom: 40
  }
}));

const Background = styled('section')(({ theme }) => ({
  backgroundColor: theme.palette.tertiary.pale,
  position: 'relative'
}));

const BgLeftStyled = styled(BgLeft)(({ theme }) => ({
  position: 'absolute',
  left: 0,
  top: 0,
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const TrollGreetingsStyled = styled('img')(({ theme }) => ({
  position: 'absolute',
  bottom: -100,
  right: 0,
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const TrollGreetingsStyledTop = styled('img')(({ theme }) => ({
  position: 'absolute',
  top: 70,
  right: -120,
  transform: 'rotate(-45deg)',
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const BgRightStyled = styled(BgRight)(({ theme }) => ({
  position: 'absolute',
  right: 0,
  bottom: 0,
  [theme.breakpoints.down('md')]: {
    display: 'none'
  }
}));

const Wrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: 24,
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column-reverse'
  }
}));

const LeftSidebar = styled('div')(() => ({
  marginBottom: 60,
  flex: 2,
  position: 'relative'
}));

const Card = styled('div')(() => ({
  backgroundColor: '#fff',
  borderRadius: 18,
  border: '1px solid rgba(10, 10, 10, 1)',
  boxShadow: '4px 4px 0px 0px rgba(10, 10, 10, 1)',
  padding: '36px 24px 26px',
  position: 'relative',
  overflow: 'hidden',
  zIndex: 1,
  cursor: 'text',
  userSelect: 'text',
  WebkitUserSelect: 'text',
  MozUserSelect: 'text',
  msUserSelect: 'text',
  '& *': {
    cursor: 'text',
    userSelect: 'text',
    WebkitUserSelect: 'text',
    MozUserSelect: 'text',
    msUserSelect: 'text'
  },
  '& button': {
    cursor: 'pointer'
  }
}));

const TagsButton = styled(Button)(() => ({
  border: '1px solid rgba(10, 10, 10, 1)',
  cursor: 'pointer',
  padding: '7px 17px',
  borderRadius: 8
}));

const ShowMoreLink = styled(Button)(({ theme }) => ({
  border: 'none',
  outline: 'none',
  boxShadow: 'none',
  textDecoration: 'underline',
  textTransform: 'none',
  padding: '7px 17px',
  minWidth: 'auto',
  fontSize: 14,
  color: theme.palette.primary.main || '#1976d2',
  cursor: 'pointer',
  '&:hover': {
    textDecoration: 'underline',
    backgroundColor: 'transparent',
    boxShadow: 'none'
  },
  '&:focus': {
    outline: 'none',
    boxShadow: 'none'
  }
}));

const BreadcrumbsWrapper = styled('div')(() => ({
  marginBottom: 24
}));

const ResultsLabel = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  paddingLeft: 3,
  color: theme.palette.textDarkGray.main,
  userSelect: 'text',
  WebkitUserSelect: 'text',
  MozUserSelect: 'text',
  msUserSelect: 'text',
  cursor: 'text',
  '& *': {
    userSelect: 'text',
    WebkitUserSelect: 'text',
    MozUserSelect: 'text',
    msUserSelect: 'text',
    cursor: 'text'
  }
}));

const List = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: `repeat(3, 1fr)`,
  gap: 24,
  marginBottom: 8,
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: `repeat(1, 1fr)`,
    gap: 16
  }
}));

const Actions = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginTop: 30,
  justifyContent: 'space-between',
  marginBottom: 16,
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 16
  }
}));

const PageDescriptionText = styled(ResultsLabel)(() => ({
  marginBottom: 20
}));

const PageDescription = styled(ResultsLabel)(() => ({
  marginTop: 48
}));

const ResultsLabelContainer = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8
}));

const IconButtonStyled = styled(IconButton)(() => ({
  padding: 0,
  margin: 3
}));

const PlatformsList = () => {
  const helmet = useTranslate('HELMET');
  const t = useTranslate('Platforms');
  const tc = useTranslate('Courses');
  const tReviews = useTranslate('Reviews');
  const tDirections = useTranslate('Directions');
  const dispatch = useDispatch();
  const isMobile = useSelector((state) => state.app.isMobile);

  const { actions, data, loading, page, perPage, count, filters, sort } = useDataList(
    platformsEndPoint,
    {
      sort: {
        programsCount: 'desc'
      }
    }
  );

  const [sortAnchorEl, setSortAnchorEl] = React.useState(null);
  const sortMenuOpen = Boolean(sortAnchorEl);

  const [query, setQuery] = React.useState(filters?.name || '');
  const [showAllDirections, setShowAllDirections] = React.useState(false);

  const handleOpenChange = React.useCallback(
    (isOpen) => {
      if (!isOpen) {
        setQuery('');
        actions.onSearchChange('');
      }
    },
    [actions]
  );

  const handleChange = React.useCallback((e) => setQuery(e.target.value), []);

  const handleSearch = React.useCallback(
    (val) => {
      const next = typeof val === 'string' ? val : query;
      actions.onSearchChange(next);
    },
    [actions, query]
  );

  React.useEffect(() => {
    setQuery(filters?.name || '');
  }, [filters?.name]);

  const handleChangePage = React.useCallback(async () => {
    actions.onChangePage(page, true, true);
  }, [actions, page]);

  const limitReached = React.useMemo(() => count <= page * perPage, [count, page, perPage]);

  const [directions, setDirections] = React.useState([]);

  React.useEffect(() => {
    dispatch(getEducationsDirections()).then((res) => {
      if (Array.isArray(res)) setDirections(res);
      else setDirections([]);
    });
  }, [dispatch]);

  const directionTags = React.useMemo(
    () =>
      (directions || []).map((topic) => {
        // Tolerate both the legacy string shape and the new {slug,name} object
        // (depending on which backend version is deployed).
        const value = topic?.slug ?? topic;
        const selected = (filters.directions || '').split(',').includes(value);
        return (
          <TagsButton
            key={value}
            variant={selected ? 'contained' : 'outlined'}
            color={selected ? 'primary' : 'inherit'}
            onClick={() =>
              actions.onFilterChange(
                {
                  ...filters,
                  directions: selected
                    ? (filters.directions || '')
                        .split(',')
                        .filter((x) => x && x !== value)
                        .join(',')
                    : [...(filters.directions ? filters.directions.split(',') : []), value]
                        .filter(Boolean)
                        .join(',')
                },
                true
              )
            }>
            {formatDirectionName(topic?.name ?? topic, tDirections)}
          </TagsButton>
        );
      }),
    [directions, actions, filters, tDirections]
  );

  // Calculate if we need to show "Show more" button
  // Desktop: ~6 tags per row, 3 rows = ~18 tags
  // Mobile: ~3 tags per row, show all (no limit on mobile)
  const maxRows = 3;
  const estimatedTagsPerRow = 6;
  const maxVisibleTags = maxRows * estimatedTagsPerRow;
  const hasMoreDirections = !isMobile && directions.length > maxVisibleTags && !showAllDirections;
  
  // Reset showAllDirections when directions change
  React.useEffect(() => {
    setShowAllDirections(false);
  }, [directions]);

  const displayedDirectionTags = React.useMemo(() => {
    if (showAllDirections || isMobile || directionTags.length <= maxVisibleTags) {
      return directionTags;
    }
    return directionTags.slice(0, maxVisibleTags);
  }, [directionTags, showAllDirections, isMobile, maxVisibleTags]);

  const platformCards = React.useMemo(
    () => (data || []).map((item, idx) => <PlatformCard key={item?._id || idx} item={item} tDirections={tDirections} />),
    [data, tDirections]
  );

  const handleSort = React.useCallback(
    (column, direction) => {
      actions.onSortChange(column, direction);
      setSortAnchorEl(null);
    },
    [actions]
  );

  const sortValues = React.useMemo(
    () => [
      { column: 'programsCount', direction: 'desc', name: tc('ByProgramsCountDesc') },
      { column: 'priority', direction: 'desc', name: t('ByPopularity') }
    ],
    [t, tc]
  );

  const { sentinelRef } = useInfiniteScroll({
    isLoading: loading,
    hasMore: !limitReached,
    onLoadMore: handleChangePage,
    rootMargin: '200px 0px',
    threshold: 0
  });

  const breadcrumbs = React.useMemo(() => [
    { link: '/', label: 'Home' },
    { link: '/education', label: 'Education' },
    { link: '/education/platforms', label: t('Title'), raw: true }
  ], [t]);

  return (
    <>
      <Helmet
        meta={{
          title: helmet('platformTitle'),
          description: helmet('platformDescription'),
          ogTitle: helmet('platformOgTitle'),
          ogDescription: helmet('platformOgDescription')
        }}
      />
      <MainLayout>
        <Header />

        <Background>
          <Content style={{ paddingBottom: 20 }}>
          <React.Suspense fallback={null}>
            <BreadcrumbsWrapper>
              <Breadcrumbs breadcrumbs={breadcrumbs} />
            </BreadcrumbsWrapper>
          </React.Suspense>
          <Card>
            <Title variant="h1">{t('Title')}</Title>

            <Actions>
              <ResultsLabel>{t('SearchPrompt')}</ResultsLabel>
              {!isMobile ? (
                <SearchToggleInput
                  query={query}
                  onChange={handleChange}
                  onSearch={handleSearch}
                  onOpenChange={handleOpenChange}
                  placeholder={t('SearchPlaceholder')}
                  openAriaLabel={t('OpenSearch')}
                  closeAriaLabel={t('CloseSearch')}
                  searchAriaLabel={t('Search')}
                />
              ) : null}
            </Actions>

            <Stack
              direction="row"
              sx={{
                flexWrap: "wrap",
                gap: '8px',
                mb: 0,
                mt: 2
              }}>
              {displayedDirectionTags}
            </Stack>
            {(hasMoreDirections || (!isMobile && showAllDirections && directionTags.length > maxVisibleTags)) && (
              <Stack
                direction="row"
                sx={{
                  justifyContent: "center",
                  width: '100%',
                  mt: 3,
                  mb: 0.5
                }}>
                {hasMoreDirections ? (
                  <ShowMoreLink onClick={() => setShowAllDirections(true)}>
                    {tReviews('ShowMore')}
                  </ShowMoreLink>
                ) : (
                  <ShowMoreLink onClick={() => setShowAllDirections(false)}>
                    {tReviews('ShowLess')}
                  </ShowMoreLink>
                )}
              </Stack>
            )}
          </Card>
        </Content>

        <Content style={{ paddingTop: 10 }}>
          <Wrapper>
            <LeftSidebar>
              {data && data.length === 0 ? (
                <Typography>{t('NoData')}</Typography>
              ) : (
                <ResultsWrapper>
                  <ResultsLabelContainer>
                    <ResultsLabel>{t('FoundResults', { count })}</ResultsLabel>
                    {loading && <CircularProgress size={16} />}
                  </ResultsLabelContainer>
                  <>
                    <ResultsControls>
                      {isMobile ? (
                        <SearchToggleInput
                          query={query}
                          onChange={handleChange}
                          onSearch={handleSearch}
                          onOpenChange={handleOpenChange}
                          placeholder={t('SearchPlaceholder')}
                          openAriaLabel={t('OpenSearch')}
                          closeAriaLabel={t('CloseSearch')}
                          searchAriaLabel={t('Search')}
                        />
                      ) : null}
                      <IconButtonStyled
                        aria-label={tc('Sort')}
                        onClick={(e) => setSortAnchorEl(e.currentTarget)}>
                        <SortIcon />
                      </IconButtonStyled>
                    </ResultsControls>
                    <Menu
                      anchorEl={sortAnchorEl}
                      open={sortMenuOpen}
                      onClose={() => setSortAnchorEl(null)}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                      {sortValues.map((item) => (
                        <MenuItem
                          key={`${item.column}-${item.direction}`}
                          selected={sort[item.column] === item.direction}
                          onClick={() => handleSort(item.column, item.direction)}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </Menu>
                  </>
                </ResultsWrapper>
              )}
              <List>{platformCards}</List>

              {!data && loading ? <CardGridSkeleton count={6} /> : null}

            {loading || !data ? <LinearProgress /> : null}

              {limitReached ? null : (
                <div ref={sentinelRef} aria-label={t('LoadMore')} style={{ height: 1 }} />
              )}

              <PageDescription component={'div'}>
                <PageDescriptionText>{t('Description1')}</PageDescriptionText>
                <PageDescriptionText>{t('Description2')}</PageDescriptionText>
                <PageDescriptionText>{t('Description3')}</PageDescriptionText>
              </PageDescription>
            </LeftSidebar>
          </Wrapper>
          <TrollGreetingsStyled src={TrollGreetings} alt="" />
        </Content>
        <TrollGreetingsStyledTop src={TrollGreetings} alt="" />
        <BgLeftStyled />
        <BgRightStyled />
        </Background>
        <Footer />
      </MainLayout>
    </>
  );
};

export default PlatformsList;
